import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import GanttTimeline from "@/components/GanttTimeline";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Palette, RotateCcw, EyeOff, CalendarOff, Printer, FileDown, Loader2, Minimize2, MonitorPlay } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { phaseColor, PMBOK_GROUPS } from "@/lib/constants";
import { differenceInCalendarDays, parseISO, startOfWeek } from "date-fns";
import DebouncedInput from "@/components/DebouncedInput";
import SwatchPicker from "@/components/SwatchPicker";
import ExportButton from "@/components/ExportButton";

const DEFAULT_TIMESCALE = "#132A5E";
const GANTT_SCALES = { fit: { label: "Fit to page" }, compact: { label: "Compact", dayW: 12 }, standard: { label: "Standard", dayW: 30 }, wide: { label: "Wide", dayW: 60 }, custom: { label: "Custom" } };

export default function TimelineView({ projectId, tasks, onOpenTask, phases, phaseColors }) {
  const ckey = `pf_swimcolors_${projectId}`;
  const [overrides, setOverrides] = useState({});
  const [holidays, setHolidays] = useState({});
  useEffect(() => {
    api.get(`/holidays?project_id=${projectId}`).then(({ data }) => setHolidays(data.holidays || {})).catch(() => {});
  }, [projectId]);
  useEffect(() => {
    try { setOverrides(JSON.parse(localStorage.getItem(ckey) || "{}")); } catch { setOverrides({}); }
  }, [ckey]);
  const setKey = (k, v) => setOverrides((o) => {
    const next = { ...o, [k]: v };
    if (!v) delete next[k];
    localStorage.setItem(ckey, JSON.stringify(next));
    return next;
  });
  const reset = () => { localStorage.removeItem(ckey); setOverrides({}); };

  const hkey = `pf_timeline_hidden_${projectId}`;
  const [hidden, setHidden] = useState({});
  useEffect(() => {
    try { setHidden(JSON.parse(localStorage.getItem(hkey) || "{}")); } catch { setHidden({}); }
  }, [hkey]);
  const toggleHidden = (id) => setHidden((prev) => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = true;
    localStorage.setItem(hkey, JSON.stringify(next));
    return next;
  });
  const showAll = () => { localStorage.removeItem(hkey); setHidden({}); };
  const hiddenCount = Object.keys(hidden).length;
  const visibleTasks = useMemo(() => tasks.filter((t) => !hidden[t.id]), [tasks, hidden]);

  const phaseList = phases && phases.length ? phases : PMBOK_GROUPS;
  const present = phaseList.filter((p) => tasks.some((t) => t.phase === p && t.start_date && t.end_date));
  const colorFor = useCallback((p) => overrides[p] || (phaseColors && phaseColors[p]) || phaseColor(p, phaseList.indexOf(p)), [overrides, phaseColors, phaseList]);
  const timescaleColor = overrides.__timescale || DEFAULT_TIMESCALE;
  const fontColor = overrides.__fontcolor || "";
  const fontSize = Number(overrides.__fontsize) || 11;
  const hideDates = !!overrides.__hidedates;
  const labelWrap = Number(overrides.__labelwrap) || 200;

  const [ganttScale, setGanttScaleState] = useState(() => {
    const s = localStorage.getItem(`pf_gantt_scale_${projectId}`);
    return GANTT_SCALES[s] ? s : "fit";
  });
  const setGanttScale = (s) => { setGanttScaleState(s); localStorage.setItem(`pf_gantt_scale_${projectId}`, s); };
  const [customW, setCustomWState] = useState(() => Number(localStorage.getItem(`pf_gantt_customw_${projectId}`)) || 30);
  const setCustomW = (w) => { setCustomWState(w); localStorage.setItem(`pf_gantt_customw_${projectId}`, String(w)); };
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const measure = () => setWrapW(wrapRef.current?.clientWidth || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const totalDays = useMemo(() => {
    const dated = visibleTasks.filter((t) => t.start_date && t.end_date);
    if (!dated.length) return 0;
    let min = parseISO(dated[0].start_date), max = parseISO(dated[0].end_date);
    dated.forEach((t) => { const s = parseISO(t.start_date), e = parseISO(t.end_date); if (s < min) min = s; if (e > max) max = e; });
    return differenceInCalendarDays(max, startOfWeek(min, { weekStartsOn: 1 })) + 5;
  }, [visibleTasks]);
  const dayW = ganttScale === "fit"
    ? Math.max(1.5, Math.floor(((wrapW || 1200) - 262) / Math.max(totalDays, 1) * 10) / 10)
    : ganttScale === "custom" ? customW : GANTT_SCALES[ganttScale].dayW;

  // Fit-on-one-page print & PDF export
  const [exportMode, setExportMode] = useState(null); // null | "print" | "pdf" | "ppt"
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pptBusy, setPptBusy] = useState(false);
  const innerRef = useRef(null);
  const [singlePage, setSinglePage] = useState(false);
  const [pageZoom, setPageZoom] = useState(1);
  useEffect(() => {
    if (!singlePage) { setPageZoom(1); return; }
    const t = setTimeout(() => {
      const el = innerRef.current, w = wrapRef.current;
      if (!el || !w) return;
      const avail = Math.max(240, window.innerHeight - w.getBoundingClientRect().top - 32);
      setPageZoom(Math.min(1, Math.max(0.25, Math.round((avail / el.scrollHeight) * 100) / 100)));
    }, 120);
    return () => clearTimeout(t);
  }, [singlePage, tasks, hidden, fontSize]);

  const captureChart = async (targetW) => {
    const node = wrapRef.current;
    return toPng(node, { backgroundColor: "#ffffff", pixelRatio: 2, width: node.scrollWidth, height: node.scrollHeight, style: { overflow: "visible" }, skipFonts: true });
  };
  const fitFor = (w) => Math.max(0.5, Math.floor((w - 262) / Math.max(totalDays, 1) * 10) / 10);
  const effDayW = exportMode === "print" ? fitFor(1000) : exportMode === "pdf" ? fitFor(1400) : dayW;

  const printTimeline = () => {
    setExportMode("print");
    document.body.classList.add("print-timeline");
    const st = document.createElement("style");
    st.id = "timeline-page-style";
    st.textContent = "@page { size: A4 landscape; margin: 8mm; }";
    document.head.appendChild(st);
    setTimeout(() => {
      window.print();
      document.body.classList.remove("print-timeline");
      document.getElementById("timeline-page-style")?.remove();
      setExportMode(null);
    }, 350);
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    setExportMode("pdf");
    await new Promise((r) => setTimeout(r, 400));
    try {
      const { jsPDF } = await import("jspdf");
      const node = wrapRef.current;
      const dataUrl = await captureChart();
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight();
      const m = 24;
      const scale = Math.min((pw - m * 2) / img.width, (ph - m * 2) / img.height);
      pdf.addImage(dataUrl, "PNG", (pw - img.width * scale) / 2, (ph - img.height * scale) / 2, img.width * scale, img.height * scale);
      pdf.save("visual-timeline.pdf");
      toast.success("Visual timeline downloaded as a one-page PDF");
    } catch (e) {
      toast.error("PDF export failed — please try again");
    } finally {
      setExportMode(null);
      setPdfBusy(false);
    }
  };

  const exportPpt = async () => {
    setPptBusy(true);
    setExportMode("ppt");
    await new Promise((r) => setTimeout(r, 400));
    try {
      const PptxGenJS = (await import("pptxgenjs")).default;
      const dataUrl = await captureChart();
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
      const pptx = new PptxGenJS();
      pptx.layout = "LAYOUT_WIDE"; // 13.33 x 7.5 in
      const slide = pptx.addSlide();
      slide.addText("Visual Timeline", { x: 0.4, y: 0.15, w: 12.5, h: 0.5, fontSize: 20, bold: true, color: "132A5E" });
      const maxW = 12.6, maxH = 6.4;
      const ratio = img.width / img.height;
      let w = maxW, h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      slide.addImage({ data: dataUrl, x: (13.33 - w) / 2, y: 0.75 + (maxH - h) / 2, w, h });
      await pptx.writeFile({ fileName: "visual-timeline.pptx" });
      toast.success("Visual timeline downloaded as PowerPoint slide");
    } catch (e) {
      toast.error("PPT export failed — please try again");
    } finally {
      setExportMode(null);
      setPptBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="timeline-view">
      <div className="flex items-center gap-2 flex-wrap justify-end">
        <div className="flex items-center gap-2 mr-auto">
          <span className="text-xs font-semibold text-muted-foreground">Timescale</span>
          <Select value={ganttScale} onValueChange={setGanttScale}>
            <SelectTrigger className="h-9 w-36" data-testid="gantt-timescale-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(GANTT_SCALES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`gantt-scale-${k}`}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {ganttScale === "fit" && <span className="text-[11px] text-muted-foreground" data-testid="gantt-fit-hint">whole timeline fits the screen</span>}
          {ganttScale === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="gantt-custom-zoom">
              <input type="range" min="1" max="60" step="0.5" value={customW} onChange={(e) => setCustomW(Number(e.target.value))} className="w-32 accent-primary" data-testid="gantt-zoom-slider" />
              <span className="text-[11px] tabular-nums text-muted-foreground w-14">{customW}px/day</span>
            </div>
          )}
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="gantt-hide-button">
              <EyeOff className="h-4 w-4" /> Hide tasks{hiddenCount ? ` (${hiddenCount})` : ""}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96" align="end" data-testid="gantt-hide-popover">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold">Show / hide on timeline</p>
              {hiddenCount > 0 && <button onClick={showAll} className="text-xs text-primary font-semibold hover:underline" data-testid="gantt-show-all">Show all</button>}
            </div>
            <p className="text-xs text-muted-foreground mb-2">Uncheck tasks to remove them from the chart. Your choices are remembered.</p>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {phaseList.map((p) => {
                const items = tasks.filter((t) => t.phase === p);
                if (!items.length) return null;
                return (
                  <div key={p}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">{p}</p>
                    <div className="space-y-0.5">
                      {items.map((t) => (
                        <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-secondary/50" data-testid={`gantt-hide-item-${t.id}`}>
                          <input type="checkbox" className="accent-primary" checked={!hidden[t.id]} onChange={() => toggleHidden(t.id)} />
                          <span className={`truncate ${hidden[t.id] ? "text-muted-foreground line-through" : ""}`}>{(t.status === "done" || (t.percent_complete || 0) >= 100) && <span className="text-emerald-500 font-bold">✓ </span>}{t.is_milestone ? "▼ " : ""}{t.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
        <Button variant="outline" size="sm" className={`gap-2 ${hideDates ? "border-primary text-primary bg-primary/5" : ""}`} onClick={() => setKey("__hidedates", hideDates ? "" : "1")} data-testid="gantt-hide-dates-toggle">
          <CalendarOff className="h-4 w-4" /> {hideDates ? "Dates hidden" : "Hide dates"}
        </Button>
        <ExportButton targetRef={wrapRef} filename="visual-timeline" label="Export" />
        <Button variant="outline" size="sm" className="gap-2" onClick={exportPdf} disabled={pdfBusy} title="Download the whole timeline as a one-page landscape PDF" data-testid="gantt-export-pdf">
          {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} PDF
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={printTimeline} title="Print the timeline fitted to one landscape page" data-testid="gantt-print-button">
          <Printer className="h-4 w-4" /> Print
        </Button>
        <Button variant="outline" size="sm" className="gap-2" onClick={exportPpt} disabled={pptBusy} title="Download as a PowerPoint slide (.pptx)" data-testid="gantt-export-ppt">
          {pptBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MonitorPlay className="h-4 w-4" />} PPT
        </Button>
        <Button variant="outline" size="sm" className={`gap-2 ${singlePage ? "border-primary text-primary bg-primary/5" : ""}`} onClick={() => setSinglePage((v) => !v)} title="Scale the whole chart to fit your screen in a single page view" data-testid="gantt-single-page-toggle">
          <Minimize2 className="h-4 w-4" /> {singlePage ? "Single page ✓" : "Single page"}
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2" data-testid="gantt-colors-button"><Palette className="h-4 w-4" /> Colors</Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end" data-testid="gantt-colors-popover">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold">Timeline colors</p>
              <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="gantt-colors-reset"><RotateCcw className="h-3 w-3" /> Reset</button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-border">
                <span className="text-sm font-semibold">Timescale bar</span>
                <SwatchPicker value={timescaleColor} onChange={(v) => setKey("__timescale", v)} title="Timescale color" testid="gantt-color-timescale" />
              </div>
              <div className="pb-2 mb-1 border-b border-border space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Font color</span>
                  <div className="flex items-center gap-1.5">
                    {fontColor && <button onClick={() => setKey("__fontcolor", "")} className="text-[10px] text-muted-foreground hover:text-foreground underline" data-testid="gantt-font-color-auto">Auto</button>}
                    <SwatchPicker value={fontColor || "#0f172a"} onChange={(v) => setKey("__fontcolor", v)} title="Font color" testid="gantt-font-color" />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold">Font size</span>
                  <div className="flex items-center gap-1.5">
                    <DebouncedInput type="range" min="9" max="18" value={fontSize} onCommit={(v) => setKey("__fontsize", v)} className="w-24" data-testid="gantt-font-size" />
                    <span className="text-xs tabular-nums text-muted-foreground w-9">{fontSize}px</span>
                  </div>
                </div>
              </div>
              {present.map((p) => (
                <div key={p} className="flex items-center justify-between gap-2">
                  <span className="text-sm truncate flex-1">{p}</span>
                  <SwatchPicker value={colorFor(p)} onChange={(v) => setKey(p, v)} title="Phase color" testid={`gantt-color-${p}`} />
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">Shared with the Roadmap view — customize once, both match.</p>
          </PopoverContent>
        </Popover>
      </div>
      <div ref={wrapRef} id="timeline-print-area" className={singlePage ? "overflow-hidden" : undefined} style={singlePage && innerRef.current ? { height: innerRef.current.scrollHeight * pageZoom + 4 } : undefined}>
        <div ref={innerRef} style={singlePage ? { transform: `scale(${pageZoom})`, transformOrigin: "top left", width: `${100 / pageZoom}%` } : undefined}>
          <GanttTimeline tasks={visibleTasks} onOpenTask={onOpenTask} phases={phases} phaseColors={phaseColors} colorOf={colorFor} timescaleColor={timescaleColor} fontColor={fontColor} fontSize={fontSize} holidays={holidays} dayW={effDayW} hideDates={hideDates} labelWrap={labelWrap} />
        </div>
      </div>
    </div>
  );
}
